import { last } from "lodash-es";
import { defineStore } from "pinia";

import { addBreadcrumb, captureMessage } from "@/error/sentry";
import type { AlmType, Id } from "@/model/baseTypes";
import type { Iteration, Session, SessionAlmStatus } from "@/model/session";
import { goToLogout } from "@/router/navigation";
import { dayDiff, isAfter, isBefore, weekDaysBetween } from "@/utils/date";

interface IterationProgress {
  daysInIteration: number;
  daysPassedInIteration: number;
  iterationsPassed: number;
}

const EMPTY_ITERATION = { id: 0, name: "", start: new Date(0), end: new Date(0) };

export const EMPTY_SESSION: Session = {
  id: "",
  name: "",
  creationDate: 0,
  planningStart: null,
  planningEnd: null,
  archived: false,
  iterations: [EMPTY_ITERATION, EMPTY_ITERATION],
  almStatus: "running",
};

export const useSessionStore = defineStore("session", {
  state: () => ({
    isSessionLoading: false,
    sessions: new Array<Session>(),
    current: EMPTY_SESSION,
    userSync: "unsyncedSession" satisfies "unsyncedSession" | "unsynced" | "synced",
    almType: "" as AlmType,
  }),
  getters: {
    iterations: (state) => state.current.iterations || [],
    uniqueSessionName: (state) => (session?: Session) => {
      if (!session) {
        session = state.current;
      }
      let index = 1;
      for (const ss of state.sessions) {
        if (ss.id === session.id) {
          break;
        }
        if (ss.name === session.name) {
          index++;
        }
      }
      return (index > 1 ? `_${index}_` : "") + session.name;
    },
    findByNameOrId: (state) => (query: string) =>
      state.sessions.find((session) => session.name === query || session.id === query),
    findByIndexedName: (state) => (sessionName: string) => {
      const indexed = /_(\d+)_(.+)/.exec(sessionName || "");
      if (indexed) {
        return state.sessions.filter((session) => session.name === indexed[2])[+indexed[1] - 1];
      }
    },
    filterArchived: (state) => (archived: boolean) =>
      archived ? state.sessions : state.sessions.filter((session) => !session.archived),
    currentlyDifferentSession() {
      return (sessionName: string) =>
        !this.current ||
        (sessionName !== this.uniqueSessionName() && sessionName !== this.current.id);
    },
    iterationProgress:
      (state) =>
      (date: Date, iteration?: Iteration): IterationProgress => {
        const iters = state.current.iterations;
        if (isBefore(date, iters[0].start)) {
          return { daysInIteration: 0, daysPassedInIteration: 0, iterationsPassed: 0 };
        }
        if (!isBefore(date, iters[iters.length - 1].end)) {
          return { daysInIteration: 0, daysPassedInIteration: 0, iterationsPassed: iters.length };
        }
        let i = iters.length - 1;
        while (i > 0 && isAfter(iters[i].start, date)) {
          i--;
        }

        if (!iteration) {
          iteration = iters[i];
        } else {
          if (iteration.id !== iters[i].id) {
            date = isBefore(date, iteration.start) ? iteration.start : iteration.end;
          }
          i = 0;
        }

        const daysInIteration = weekDaysBetween(iteration.start, iteration.end);
        const daysPassedInIteration = Math.min(
          daysInIteration,
          weekDaysBetween(iteration.start, date),
        );

        return {
          daysInIteration,
          daysPassedInIteration,
          iterationsPassed: i + daysPassedInIteration / daysInIteration,
        };
      },
    /**
     * Get the name for the given iteration, by id
     *
     * @param id iteration number (0-based index)
     * @returns iteration name
     */
    getIterationName:
      (state) =>
      (id?: number | null): string => {
        if (id === null || id === undefined) return "";
        return state.current.iterations.find((i) => i.id === id)?.name || "";
      },
    getIterationById:
      (state) =>
      (id?: number | null): Iteration | undefined => {
        if (id === null || id === undefined) return undefined;
        return state.current.iterations.find((i) => i.id === id) || undefined;
      },
    currentIteration: (state) => currentIteration(state.current.iterations, true),
    nearestIteration: (state) => currentIteration(state.current.iterations, false),
    fullDaysSinceStart: (state): number | undefined => {
      const iterations = state.current.iterations;
      if (iterations?.length) {
        return Math.floor(dayDiff(iterations[0].start, new Date()));
      }
    },
    sessionHasStarted(): boolean {
      return (this.fullDaysSinceStart || -1) >= 0;
    },
  },
  actions: {
    setSessions(sessions: Session[]) {
      // if we are in a session, try to find it in the list
      // set current/selected if session is found or logout if not
      this.sessions = sessions;
      if (this.current.id) {
        const currentSession = sessions.find((session) => session.id === this.current.id);
        if (!currentSession) {
          void goToLogout("no-session");
        } else {
          this.current = currentSession;
        }
      }
    },
    setSessionAlmStatus(status: SessionAlmStatus) {
      this.current.almStatus = status;
    },
    setAlmType(type: AlmType) {
      this.almType = type;
    },
    setSession({ id: sessionId }: Id) {
      addBreadcrumb("store", {
        message: `Setting current and selected session to ${sessionId}`,
        data: { sessionId },
      });
      this.current = this.sessions.find((session) => session.id === sessionId) || EMPTY_SESSION;
      if (this.current.id === EMPTY_SESSION.id) {
        const sessionIds = this.sessions.map(({ id }) => id);
        captureMessage("Could not find the Session ID in the list of active sessions", {
          info: { sessionId, availableSessions: sessionIds },
        });
      }
    },
  },
});

function currentIteration(iterations: Iteration[], strict: boolean) {
  if (isBefore(new Date(), iterations[0].start)) {
    return strict ? undefined : iterations[0];
  }
  if (isAfter(new Date(), iterations[iterations.length - 1].end)) {
    return strict ? undefined : last(iterations);
  }
  // iteration dates might overlap, in this case, return the latest
  return iterations.findLast(
    (iter) => isAfter(new Date(), iter.start) && isBefore(new Date(), iter.end),
  )!;
}
